import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setEvaluatorPhase, setExamMode, resetExam } from '../features/examSlice';
import { logout, selectUser } from '../features/authSlice';
import { authFetch } from '../services/authApi';
import { useActiveCompetitions } from '../hooks/useActiveCompetitions';
import { EXAM_REGISTRY, PROVIDER_INFO } from '../data/examRegistry';
import { BRAND_BLUE } from '../brand';

const formatCountdown = (endsAt) => {
  if (!endsAt) return null;
  const ms = new Date(endsAt).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return 'Ending soon';
  const days = Math.floor(ms / 86400000);
  if (days >= 1) return `${days}d left`;
  const hours = Math.floor(ms / 3600000);
  if (hours >= 1) return `${hours}h left`;
  return `${Math.max(1, Math.floor(ms / 60000))}m left`;
};

// Simple icon components (no external dependencies)
const Icons = {
  Lock: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  ),
  Star: () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="2">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  ),
  Play: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
      <polygon points="5 3 19 12 5 21 5 3"/>
    </svg>
  ),
  Swords: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5"/>
      <line x1="13" x2="19" y1="19" y2="13"/>
      <line x1="16" x2="20" y1="16" y2="20"/>
      <line x1="19" x2="21" y1="21" y2="19"/>
      <polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5"/>
      <line x1="5" x2="9" y1="14" y2="18"/>
      <line x1="7" x2="4" y1="17" y2="20"/>
      <line x1="3" x2="5" y1="19" y2="21"/>
    </svg>
  ),
  GraduationCap: ({ color }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color || "currentColor"} strokeWidth="2">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
      <path d="M6 12v5c3 3 9 3 12 0v-5"/>
    </svg>
  ),
  Trophy: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
      <path d="M4 22h16"/>
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
    </svg>
  ),
  History: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
      <path d="M3 3v5h5"/>
      <path d="M12 7v5l4 2"/>
    </svg>
  ),
  BarChart3: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 3v18h18"/>
      <path d="M7 16v-5M12 16V8M17 16v-3"/>
    </svg>
  ),
  Zap: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  ),
  Shield: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>
    </svg>
  ),
  LogOut: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/>
      <line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
};

const ExamCard = ({ exam, onClick, primaryColor }) => {
  const isLocked = !exam.unlocked;

  return (
    <button
      onClick={onClick}
      disabled={isLocked}
      className={`group w-full text-left bg-white rounded-2xl border border-slate-200 px-7 py-8 transition-all duration-200 ${
        isLocked
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:border-slate-300 hover:shadow-md active:scale-[0.99]'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5">
            {isLocked && (
              <span className="text-slate-400 flex-shrink-0"><Icons.Lock /></span>
            )}
            <h3 className="font-bold text-slate-800 text-2xl leading-tight truncate">
              {exam.state}
            </h3>
            {exam.bestScore != null && (
              <span className="text-sm font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full flex-shrink-0">
                {exam.bestScore}%
              </span>
            )}
          </div>
          <p className="text-base text-slate-400 mt-2">
            For nurse aide test takers in {exam.state}
          </p>
        </div>
        <span
          className="text-sm font-semibold text-white px-3 py-1.5 rounded-full flex-shrink-0 mt-0.5"
          style={{ backgroundColor: primaryColor }}
        >
          {exam.providerLabel}
        </span>
      </div>
    </button>
  );
};

export default function ExamHome() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  const org = user?.organization;
  const primaryColor = org?.primary_color || '#3b86f5';

  const [bestScores, setBestScores] = useState({});
  const [sortBy, setSortBy] = useState('state'); // 'state' | 'provider'
  const [modeExam, setModeExam] = useState(null); // exam selected for the mode chooser
  const { competitions, participates } = useActiveCompetitions('skill_exam');
  const activeCompetition = competitions[0] || null;

  // Fetch best scores from past exam attempts
  useEffect(() => {
    authFetch('/api/scoring/my-exams/')
      .then((data) => {
        const attempts = data.results || data || [];
        const scores = {};
        for (const attempt of attempts) {
          if (attempt.status !== 'completed' || attempt.total_score == null) continue;
          // Build a registry-compatible key from state_code + provider
          const key = attempt.provider && attempt.state_code
            ? `${attempt.state_code.toLowerCase()}-${attempt.provider}`
            : attempt.state_code?.toLowerCase();
          // Keep highest score per exam
          if (!scores[key] || attempt.total_score > scores[key]) {
            scores[key] = attempt.total_score;
          }
        }
        setBestScores(scores);
      })
      .catch(() => {});
  }, []);

  const isAdmin = user?.role === 'admin';
  const isSuperAdmin = !!user?.is_staff;

  const exams = useMemo(() => {
    const orgExamIds = org?.exam_ids;
    let list;
    if (orgExamIds && orgExamIds.length > 0) {
      list = EXAM_REGISTRY.filter(e => orgExamIds.includes(e.id))
        .map(e => ({ ...e, unlocked: true }));
    } else {
      list = EXAM_REGISTRY;
    }
    // Attach best scores
    return list.map(e => {
      const key1 = e.provider && e.stateCode
        ? `${e.stateCode.toLowerCase()}-${e.provider}`
        : e.stateCode?.toLowerCase();
      const key2 = e.id;
      return { ...e, bestScore: bestScores[key1] || bestScores[key2] || null };
    });
  }, [org, bestScores]);

  const sortedExams = useMemo(() => {
    const sorted = [...exams];
    if (sortBy === 'provider') {
      sorted.sort((a, b) => a.provider.localeCompare(b.provider) || a.state.localeCompare(b.state));
    } else {
      sorted.sort((a, b) => a.state.localeCompare(b.state));
    }
    return sorted;
  }, [exams, sortBy]);

  const totalBestScore = exams.reduce(
    (sum, e) => sum + (e.bestScore || 0),
    0
  );

  const handleExamSelect = (exam) => {
    if (!exam.unlocked) return;
    setModeExam(exam);
  };

  const handleStartMode = (mode) => {
    const exam = modeExam;
    if (!exam) return;
    setModeExam(null);
    dispatch(resetExam());
    dispatch(setExamMode(mode));
    dispatch(setEvaluatorPhase(mode === 'practice' ? 'picking' : 'welcome'));
    navigate(`/exam/${exam.id}`);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const orgName = org?.name || 'Skillform';

  return (
    <div className="min-h-screen bg-slate-50 overflow-y-auto">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white text-slate-800 shadow-lg border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              {(org?.logo_small || org?.logo) ? (
                <img
                  src={org.logo_small || org.logo}
                  alt={org.name}
                  className="w-10 h-auto"
                />
              ) : (
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
                  style={{ background: primaryColor }}
                >
                  <Icons.GraduationCap color="white" />
                </div>
              )}
              <div>
                <h1 className="font-bold text-xl leading-tight text-slate-800">
                  {orgName}
                </h1>
                <p className="text-sm text-slate-500">Exam Simulator</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-2 text-slate-700">
                <Icons.Zap className="text-slate-800" />
                <span className="text-base font-bold text-emerald-600">{totalBestScore}</span>
              </div>
              {(isAdmin || isSuperAdmin) && (
                <button
                  onClick={() => navigate('/admin/members')}
                  className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-all"
                  title="Manage members"
                >
                  <Icons.Shield />
                </button>
              )}
              {isSuperAdmin && (
                <button
                  onClick={() => navigate('/')}
                  className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-all"
                  title="Back to Admin"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 18l-6-6 6-6"/>
                  </svg>
                </button>
              )}
              <button
                onClick={handleLogout}
                className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-all"
                title={user ? `Sign out (${user.email})` : 'Sign out'}
              >
                <Icons.LogOut />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Active competition hero (only when the org has a live event) */}
        {activeCompetition && (
          <button
            onClick={() => navigate(`/competitions/${activeCompetition.id}`)}
            className="relative mb-5 w-full text-left rounded-2xl overflow-hidden text-white shadow-xl transition-all active:scale-[0.99]"
          >
            {activeCompetition.image ? (
              <img
                src={activeCompetition.image}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0" style={{ background: BRAND_BLUE }} />
            )}
            <div
              className="absolute inset-0"
              style={{ background: `linear-gradient(to top, ${BRAND_BLUE}f2 0%, ${BRAND_BLUE}bf 45%, ${BRAND_BLUE}40 100%)` }}
            />
            <div className="relative p-5 min-h-[184px] flex flex-col justify-between">
              <div className="flex items-center justify-between gap-3">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide bg-white/20 backdrop-blur px-2.5 py-1 rounded-full">
                  <Icons.Trophy />
                  Live Competition
                </span>
                {formatCountdown(activeCompetition.ends_at) && (
                  <span className="text-sm font-semibold bg-black/25 backdrop-blur px-2.5 py-1 rounded-full">
                    {formatCountdown(activeCompetition.ends_at)}
                  </span>
                )}
              </div>
              <div>
                <h3 className="font-bold text-2xl leading-tight drop-shadow-sm">{activeCompetition.name}</h3>
                {activeCompetition.sponsor_name && (
                  <p className="text-sm text-white/80 mt-1">Sponsored by {activeCompetition.sponsor_name}</p>
                )}
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-white/90">
                    {activeCompetition.my_rank
                      ? `You're ranked #${activeCompetition.my_rank}`
                      : 'Not entered yet'}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-sm font-bold bg-white text-slate-800 px-4 py-2 rounded-full shadow-sm">
                    <Icons.Swords />
                    Compete
                  </span>
                </div>
              </div>
            </div>
          </button>
        )}

        {/* Quick actions */}
        <div className={`mb-8 grid ${participates ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}>
          {[
            { key: 'leaderboard', label: 'Leaderboard', Icon: Icons.BarChart3, to: '/leaderboard', badge: 0 },
            ...(participates
              ? [{ key: 'competitions', label: 'Competitions', Icon: Icons.Trophy, to: '/competitions', badge: competitions.length }]
              : []),
            { key: 'history', label: 'History', Icon: Icons.History, to: '/history', badge: 0 },
          ].map(({ key, label, Icon, to, badge }) => (
            <button
              key={key}
              onClick={() => navigate(to)}
              className="relative flex flex-col items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-4 transition-all hover:border-slate-300 hover:shadow-sm active:scale-[0.98]"
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: primaryColor + '14', color: primaryColor }}
              >
                <Icon />
              </div>
              <span className="text-xs font-semibold text-slate-700">{label}</span>
              {badge > 0 && (
                <span className="absolute top-2.5 right-2.5 min-w-5 h-5 px-1 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center justify-center">
                  {badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Section header with sort */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-bold text-slate-800 text-2xl">Choose an Exam</h2>
            <p className="text-slate-500 text-base mt-0.5">
              {exams.length} exams • CNA certification practice
            </p>
          </div>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
            <button
              onClick={() => setSortBy('state')}
              className={`text-sm font-semibold px-3 py-1.5 rounded-md transition-all ${
                sortBy === 'state'
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              State
            </button>
            <button
              onClick={() => setSortBy('provider')}
              className={`text-sm font-semibold px-3 py-1.5 rounded-md transition-all ${
                sortBy === 'provider'
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Provider
            </button>
          </div>
        </div>

        {/* Exams Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {sortedExams.map((exam) => (
            <ExamCard
              key={exam.id}
              exam={exam}
              primaryColor={primaryColor}
              onClick={() => handleExamSelect(exam)}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="text-center mt-12 pb-10">
          <p className="text-slate-400 text-base">Skillform AI</p>
        </div>
      </main>

      {/* Mode chooser modal */}
      {modeExam && (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-4"
          onClick={() => setModeExam(null)}
        >
          <div
            className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-1">
              <div>
                <h3 className="font-bold text-slate-800 text-2xl leading-tight">{modeExam.state}</h3>
                <p className="text-base text-slate-400 mt-0.5">How do you want to practice?</p>
              </div>
              <button
                onClick={() => setModeExam(null)}
                className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 hover:bg-slate-200 flex-shrink-0"
                title="Close"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="mt-5 space-y-3">
              <button
                onClick={() => handleStartMode('full')}
                className="w-full text-left rounded-xl px-5 py-4 text-white shadow-lg transition-all active:scale-[0.99]"
                style={{ background: primaryColor }}
              >
                <div className="flex items-center justify-between">
                  <span className="font-bold text-lg">Full Exam</span>
                  <span className="text-sm font-semibold bg-white/20 px-2.5 py-0.5 rounded-full">3 credits</span>
                </div>
                <p className="text-sm text-white/80 mt-1">5 randomized skills, full review before submitting</p>
              </button>

              <button
                onClick={() => handleStartMode('practice')}
                className="w-full text-left rounded-xl border border-slate-200 bg-white px-5 py-4 transition-all hover:bg-slate-50 active:scale-[0.99]"
              >
                <div className="flex items-center justify-between">
                  <span className="font-bold text-lg text-slate-800">Practice One Skill</span>
                  <span className="text-sm font-semibold text-slate-500 bg-slate-100 px-2.5 py-0.5 rounded-full">1 credit</span>
                </div>
                <p className="text-sm text-slate-400 mt-1">Pick a single skill and get instant AI feedback</p>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
